import Vue from 'vue'
import $store from '@/store'
import $router from '@/router'
import $hasRole from '@/utils/role'
import { getCompanyVerify } from '@/views/admin/company/service/service'
import { idCardAudit } from '@/views/user/account/information/service/service'
import { getAccountPackage } from '@/views/user/fee/service'

/**
 * 用户数据检测
 */
class UserCheck {
  /**
   * 获取用户信息
   * @returns {*}
   */
  get userInfo() {
    return $store.state.userInfo
  }

  /**
   * 是否个人身份（企微无）
   * @returns {boolean}
   */
  get isPerson() {
    return false
  }

  /**
   * 个人是否实名认证
   * @returns {boolean}
   */
  get isPersonCertified() {
    return this.userInfo.status === 2
  }

  /**
   * 个人实名是否身份证类型
   * @return {boolean}
   */
  get isID() {
    return this.userInfo.certType === '0'
  }

  /**
   * 是否企业身份
   * @returns {boolean}
   */
  get isCompany() {
    return !this.isPerson
  }

  /**
   * 是否超级管理员
   * @returns {boolean}
   */
  get isSuperAdmin() {
    return this.isCompany && this.userInfo.superAdminFlg
  }

  /**
   * 是否子管理员
   * @returns {boolean}
   */
  get isMinAdmin() {
    return this.isCompany && this.userInfo.minAdminFlg
  }

  /**
   * 是否普通员工
   * @returns {boolean}
   */
  get isStaff() {
    return this.isCompany && !this.userInfo.superAdminFlg
  }

  /**
   * 企业是否实名认证
   * @returns {*}
   */
  get isCompanyCertified() {
    return this.userInfo.companyStatus === 4
  }

  /**
   * 是否为迁移数据
   * @return {boolean}
   */
  get isMigration() {
    return !!this.userInfo.isMigration
  }

  /**
   * 是否迁移数据待补充实名
   */
  get needReCertified() {
    return this.userInfo.migrationStatus === 2
  }

  // 是否授权印章
  get hasSign() {
    return this.userInfo.hasSign
  }

  // 是否显示创建企业按钮公共判断
  get hasCreateCompanyBtn() {
    // 个人已实名且为身份证类型
    // 个人未实名
    return (this.isPersonCertified && this.isID) || !this.isPersonCertified
  }
}

class Auth extends UserCheck {
  /**
   * 检测用户是否实名认证
   * @param returnUrl 认证后回跳的地址
   * @returns {boolean}
   */
  checkPerson(returnUrl = window.location.href) {
    if (this.isPersonCertified) return true

    const { accountId, isMigration } = this.userInfo
    const message = isMigration
      ? '法大大官网升级了，为了给您提供更加优质与安全的服务，请重新进行实名认证'
      : '您尚未实名认证，请先扫码认证'

    Vue.prototype
      .$alert({
        title: '实名认证提示',
        message,
        confirmButtonText: '去认证'
      })
      .then(() => {
        idCardAudit({
          accountId,
          returnUrl
        }).then(res => {
          if (res.success) {
            location.href = res.data
          } else {
            Vue.prototype.$error(res.message)
          }
        })
      })
      .catch(error => {
        error && console.error(error)
      })

    return false
  }

  /**
   * 企业实名认证检测
   * @param returnUrl 认证后回跳的地址
   * @returns {boolean}
   */
  checkCompany(returnUrl = window.location.href) {
    if (!(this.isCompany && !this.isCompanyCertified)) return true

    if (this.isID && this.isSuperAdmin) {
      Vue.prototype
        .$alert({
          title: '企业未实名认证',
          message: '请先完成企业实名认证，可享受更多权益',
          confirmButtonText: '去认证'
        })
        .then(() => {
          let { companyId } = this.userInfo
          getCompanyVerify({ companyId, returnUrl }).then(res => {
            if (res.success) {
              location.href = res.data
            } else {
              Vue.prototype.$error(res.message)
            }
          })
        })
        .catch(e => {
          e && console.log(e)
        })
    } else {
      Vue.prototype.$alert({ title: '企业未实名认证', message: '请先联系管理员完成企业实名认证，可享受更多权益' })
    }

    return false
  }

  /**
   * 同时检测个人和企业认证
   * @param returnUrl 认证后回跳的地址
   * @returns {boolean}
   */
  checkAllCertified(returnUrl = window.location.href) {
    return this.checkPerson(returnUrl) && this.checkCompany(returnUrl)
  }

  /**
   * 检测是否有印章
   * @param {object} btn 没有授权时操作按钮 { text, handler }，handler 在立即设置按钮时也会并行触发
   * @memberof Auth
   */
  checkSign(btn) {
    if (this.hasSign) return true

    this.signAlert(btn)

    return false
  }

  /**
   * 未授权印章警告
   * @param {object} btn 没有授权时操作按钮 { text, confirmText, handler }，handler 在立即设置按钮时也会并行触发
   * @memberof Auth
   */
  async signAlert(btn) {
    // 有印章管理权限
    if ($hasRole('seal')) {
      await Vue.prototype.$confirm({
        message: '您尚未获得印章授权，请前往控制后台进行设置。',
        confirmButtonText: (btn && btn.confirmText) || '立即设置'
      })

      if (btn && btn.handler) btn.handler()
      window.open('/main/stampList')
    } else {
      await Vue.prototype.$confirm({
        message: '您尚未获得印章授权，请联系管理员授权。',
        confirmButtonText: (btn && btn.text) || '确 定'
      })

      if (btn && btn.handler) btn.handler()
    }
  }

  /**
   * 检测是否有套餐(本地信息获取)
   * @param {object} btn 操作按钮，参考 packageAlert
   * @memberof Auth
   * @returns {Boolean}
   */
  checkPackage(btn) {
    const { hasProduct } = this.userInfo
    if (!hasProduct) this.packageAlert(btn)
    return hasProduct
  }

  /**
   * 检测是否有足够套餐份数(异步)
   * @param {number} [need=1] 需要的份数
   * @param {object} btn 操作按钮，参考 packageAlert
   * @memberof Auth
   * @returns {Promise}
   */
  async checkPackageAsync(need = 1, btn) {
    // 加载套餐出错放行
    let remain

    try {
      remain = (await getAccountPackage()).remainNum
    } catch (e) {
      return true
    }

    if (remain >= (need >> 0 || 1)) return true

    this.packageAlert(btn)

    return Promise.reject()
  }

  /**
   * 套餐不足警告
   * @param {object} btn 没有授权时操作按钮 { text, handler, confirmText }，handler 在立即设置按钮时也会并行触发
   * @memberof Auth
   */
  async packageAlert(btn) {
    if (this.isSuperAdmin) {
      await Vue.prototype.$confirm({
        message: '套餐余额不足，请先购买套餐。',
        confirmButtonText: (btn && btn.confirmText) || '立即购买'
      })

      if (btn && btn.handler) btn.handler()
      window.open('https://docs.qq.com/doc/DQ2d0WUR6aFVnTHRK')
    } else {
      await Vue.prototype.$confirm({
        message: '套餐余额不足，请联系管理员购买套餐。',
        confirmButtonText: (btn && btn.text) || '确 定'
      })

      if (btn && btn.handler) btn.handler()
    }
  }

  /**
   * 签署统一处理提示（无套餐/无印章）
   * @param code
   * @param message
   */
  sendPrompt(code, message) {
    switch (code >> 0) {
      case 110097: //没有授权印章无法发起签署
        this.signAlert()
        break
      case 110098: //套餐不足无法发起签署
        this.packageAlert()
        break
      default:
        message && Vue.prototype.$error(message)
    }
  }
}

const $auth = new Auth()

Vue.prototype.$auth = $auth

export default $auth
